import { useEffect } from 'react';

interface PageSEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = "https://biobeautezen.com/images/og-image.jpg";
const SITE_NAME = "Bio Beauté Zen";

const usePageSEO = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  noindex = false
}: PageSEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to update or create meta tag
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Update meta description
    updateMetaTag('description', description);

    // Update canonical if provided
    if (canonical) {
      updateLinkTag('canonical', canonical);
    }

    // Update robots meta if noindex
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists and noindex is false
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Update Open Graph tags
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:image', ogImage || DEFAULT_OG_IMAGE, true);
    updateMetaTag('og:site_name', SITE_NAME, true);
    updateMetaTag('og:type', 'website', true);
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);
    updateMetaTag('twitter:image', ogImage || DEFAULT_OG_IMAGE);

    return () => {
      // Reset to default title on unmount
      document.title = 'Bio Beauté Zen | Institut de beauté bio à Montpellier';
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, noindex]);
};

export default usePageSEO;
